import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/shared/ui/components/ui/dialog";
import { Button, type ButtonVariant } from "./Button";
import { cn } from "@/shared/lib/utils";
import { IconX } from "@/assets/svgs/iconX";

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  onCancel?: () => void;
  onConfirm?: () => void;
  cancelLabel?: string;
  confirmLabel?: string;
  children?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  subPadding?: string;
  confirmButtonVariant?: ButtonVariant;
  subTitle?: string;
  confirmButtonIcon?: React.ReactNode;
  disabled?: boolean;
  fullWidthButtons?: boolean;
  showDescriptionBorder?: boolean;
  descriptionClassName?: string;
  subTitleClassName?: string;
  IconPosition?: "left" | "right";
  cancelButtonIcon?: React.ReactNode;
  isLoading?: boolean;
  secondSubTitle?: string;
  footerAlign?: "stretch" | "end";
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onOpenChange,
  title,
  description,
  icon,
  onCancel,
  onConfirm,
  cancelLabel = "Cancel",
  confirmLabel = "Primary Action",
  children,
  className,
  contentClassName,
  confirmButtonVariant,
  subTitle,
  confirmButtonIcon,
  disabled,
  fullWidthButtons = false,
  showDescriptionBorder = true,
  descriptionClassName,
  subTitleClassName,
  subPadding = "",
  IconPosition = "left",
  cancelButtonIcon,
  isLoading,
  secondSubTitle,
  footerAlign = "stretch",
}) => {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onOpenChange(false);
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "min-w-[400px] w-fit p-0 gap-0 rounded-xl shadow-[0_1px_4px_0_rgba(0,0,0,0.1)] overflow-auto",
          contentClassName,
        )}
      >
        <DialogClose className="absolute right-[10px] top-[6px] rounded-xl transition-opacity outline-none disabled:pointer-events-none rtl:left-[10px] rtl:right-auto">
          <IconX color="text-grey-900" size={20} />
        </DialogClose>

        <DialogHeader className="px-6 py-2">
          <div className="flex  flex-col items-start gap-4">
            {icon && <div className="flex-shrink-0 mt-1">{icon}</div>}
            <div className="flex-1">
              <DialogTitle className="text-lg font-bold text-left text-grey-900">
                {title}
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <div className={cn("px-6 pb-4 flex flex-col gap-4", subPadding)}>
          {subTitle && (
            <DialogDescription
              className={cn(
                "text-xs font-semibold text-grey-900",
                subTitleClassName,
                subPadding,
              )}
            >
              {subTitle}
            </DialogDescription>
          )}
          {description && (
            <DialogDescription
              className={cn(
                "text-xs font-semibold text-grey-400 pb-4",
                showDescriptionBorder && "border-b border-grey-100",
                descriptionClassName,
              )}
            >
              {description}
            </DialogDescription>
          )}
          {secondSubTitle && (
            <DialogDescription
              className=
              "text-xs font-semibold text-grey-900 border-b border-grey-100 pb-4"
            >
              {secondSubTitle}
            </DialogDescription>
          )}
        </div>

        {children && (
          <div className={cn("px-6 pb-6", className)}>{children}</div>
        )}

        <div
          className={cn(
            "flex items-center gap-3 px-4 pb-6",
            fullWidthButtons
              ? "flex-col"
              : footerAlign === "end"
                ? "justify-end"
                : "w-full",
          )}
        >
          {cancelLabel && (
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={disabled || isLoading}
              className={cn(
                "px-6 py-4",
                fullWidthButtons
                  ? "w-full"
                  : footerAlign === "end"
                    ? "w-fit"
                    : "min-w-0 flex-1",
              )}
            >
              {cancelButtonIcon && (
                <div className="flex-shrink-0">{cancelButtonIcon}</div>
              )}
              {cancelLabel}
            </Button>
          )}
          {confirmLabel && (
            <Button
              variant={confirmButtonVariant || "black-grey"}
              onClick={handleConfirm}
              disabled={disabled || isLoading}
              isLoading={isLoading}
              className={cn(
                "px-6 py-4",
                fullWidthButtons
                  ? "w-full"
                  : footerAlign === "end"
                    ? "w-fit"
                    : "min-w-0 flex-1",
              )}
            >
              {IconPosition === "left" && confirmButtonIcon && (
                <div className="flex-shrink-0">{confirmButtonIcon}</div>
              )}
              {confirmLabel}
              {IconPosition === "right" && confirmButtonIcon && (
                <div className="flex-shrink-0">{confirmButtonIcon}</div>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

Modal.displayName = "Modal";
